/**
 * MetaConversationPricingForm — create dialog body for
 * `/admin/pricing/meta-categories`.
 *
 * Meta cobra por conversa (janela de 24h), categorizada em MARKETING /
 * UTILITY / AUTHENTICATION / SERVICE. Preço é sempre cadastro manual.
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type MetaConversationPricingCategory =
  | 'MARKETING'
  | 'UTILITY'
  | 'AUTHENTICATION'
  | 'SERVICE';

export interface MetaConversationPricingFormValue {
  category: MetaConversationPricingCategory;
  pricePerConversation: number | null;
}

export const DEFAULT_META_PRICING_FORM: MetaConversationPricingFormValue = {
  category: 'UTILITY',
  pricePerConversation: null,
};

const CATEGORIES: MetaConversationPricingCategory[] = [
  'MARKETING',
  'UTILITY',
  'AUTHENTICATION',
  'SERVICE',
];

interface Props {
  submitting?: boolean;
  onSubmit: (next: {
    category: MetaConversationPricingCategory;
    pricePerConversation: number;
  }) => void;
  onCancel: () => void;
}

export function MetaConversationPricingForm({ submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<MetaConversationPricingFormValue>(DEFAULT_META_PRICING_FORM);

  const isValid = form.pricePerConversation !== null && form.pricePerConversation >= 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || form.pricePerConversation === null) return;
    onSubmit({ category: form.category, pricePerConversation: form.pricePerConversation });
  }

  const disabled = !isValid || !!submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="meta-pricing-category">Categoria</Label>
        <select
          id="meta-pricing-category"
          aria-label="Categoria"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={form.category}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              category: e.target.value as MetaConversationPricingCategory,
            }))
          }
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="meta-pricing-price">Preço por conversa (USD)</Label>
        <Input
          id="meta-pricing-price"
          aria-label="Preço por conversa (USD)"
          type="number"
          step={0.001}
          min={0}
          value={form.pricePerConversation ?? ''}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              pricePerConversation: e.target.value === '' ? null : Number(e.target.value),
            }))
          }
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Vale a partir de agora. Preço antigo não é sobrescrito — fica no histórico.
      </p>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={!!submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          Cadastrar preço
        </Button>
      </div>
    </form>
  );
}
